import { datadogRum } from '@datadog/browser-rum';
import * as Sentry from '@sentry/react';
import { useAuth } from '@teamexos/fit-shared';
import React, { lazy, Suspense, useEffect } from 'react';
import {
  createBrowserRouter,
  createMemoryRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router';
import Classes from '../components/Classes';
import { FloatingAlerts } from '../components/FloatingAlerts';
import { GameplanTemplates } from '../components/MemberProfile/MemberGameplans/GameplanTemplates/GameplanTemplates';
import { CustomizeTrainingPlanTemplate } from '../components/MemberProfile/TrainingPlans/CustomizeTrainingPlanTemplate';
import { TrainingPlanTemplates } from '../components/MemberProfile/TrainingPlans/TrainingPlanTemplates';
import PrivateRoute from '../components/PrivateRoute';
import { LoadingPage } from '../components/reusable/LoadingPage';
import { SnackbarVariant } from '../components/reusable/Snackbar';
import { useSnackbarContext } from '../components/reusable/Snackbar/SnackbarContextProvider';
import SignIn from '../components/SignIn';
import { CoachType, useSelfQuery } from '../graphql/types';
import useLogout from '../hooks/useLogout';
import { mixpanelClient } from '../utils/mixpanelClient';
import { ErrorBoundary } from './ErrorBoundary';
import Playground from './playground';

const GamePlanEditor = lazy(() =>
  import('../components/MemberProfile/MemberGameplans/GameplanEditor').then(
    (module) => ({ default: module.GamePlanEditor }),
  ),
);

const CoachSettings = lazy(() =>
  import('../components/CoachSettings').then((module) => ({
    default: module.CoachSettings,
  })),
);

const MemberGate = lazy(() => import('../components/MemberGate'));
const HomePage = lazy(() => import('../components/HomePage'));
const Member = lazy(() => import('../components/MemberProfile'));
const Members = lazy(() => import('../components/Members'));
const Sessions = lazy(() => import('../components/Sessions'));
const Methodology = lazy(() => import('../components/Methodology'));

const PageSuspense: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Suspense fallback={<LoadingPage />}>{children}</Suspense>
);

const MainScreen: React.FC = () => {
  const { data, loading } = useSelfQuery();

  const coachType = data?.self?.coachData?.type;

  if (loading) {
    return null;
  }

  if (coachType === CoachType.Coach || coachType === CoachType.Dietitian) {
    return <HomePage />;
  }

  return <MemberGate />;
};

const AppWrapper = () => {
  const { isLoggedIn, isLoading: isAuthLoading, error: authError } = useAuth();
  const { setSnackbar } = useSnackbarContext();
  const logout = useLogout();
  const { data } = useSelfQuery({
    skip: !isLoggedIn || isAuthLoading,
  });

  useEffect(() => {
    if (
      authError?.networkError &&
      'result' in authError.networkError &&
      typeof authError.networkError.result === 'object' &&
      authError.networkError.result.errors
    ) {
      const isUnauthenticated = authError.networkError.result.errors.some(
        (error: { message: string; extensions: Record<string, string> }) =>
          error.message.includes('User inactive') ||
          error.extensions.code === 'UNAUTHENTICATED',
      );
      if (isUnauthenticated) {
        const error = new Error('User inactive/unauthenticated, logging out');
        Sentry.captureException(error);
        datadogRum.addError(error, {
          error: authError,
        });
        setSnackbar({
          message:
            'Authentication error. Contact support if this issue persists.',
          variant: SnackbarVariant.Danger,
        });
        logout();
        return;
      }
    }

    if (!data?.self?.id) {
      return;
    }

    datadogRum.setUser({
      id: data.self.id,
    });
    Sentry.setUser({ id: data.self.id });
    mixpanelClient.identify(data.self.id);
  }, [data?.self?.id, authError, logout, setSnackbar]);

  return (
    <>
      {isLoggedIn && <FloatingAlerts />}
      <Outlet />
    </>
  );
};

const routesConfig = createRoutesFromElements(
  <Route path="/" ErrorBoundary={ErrorBoundary}>
    {/* Redirect from / to /app */}
    <Route index element={<Navigate to="/app" replace />} />
    <Route path="app">
      <Route path="login" element={<SignIn />} />
      <Route
        path="playground"
        element={
          process.env.GATSBY_ENV_LEVEL === 'dev' ? (
            <Playground />
          ) : (
            <Navigate to="/app" replace />
          )
        }
      />
      <Route element={<AppWrapper />}>
        <Route
          index
          element={
            <PrivateRoute>
              <PageSuspense>
                <MainScreen />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="fitMembersList"
          path="members"
          element={
            <PrivateRoute allowedCoachTypes={[CoachType.Coach]}>
              <PageSuspense>
                <Members />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="membersProfile"
          path="members/:memberId"
          element={
            <PrivateRoute
              allowedCoachTypes={[CoachType.Coach, CoachType.Dietitian]}
            >
              <PageSuspense>
                <Member />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="gameplanCreator"
          path="members/:memberId/gameplan"
          element={
            <PrivateRoute allowedCoachTypes={[CoachType.Coach]}>
              <PageSuspense>
                <GamePlanEditor state="create" />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="gameplanViewer"
          path="members/:memberId/gameplan/:planId"
          element={
            <PrivateRoute
              allowedCoachTypes={[CoachType.Coach, CoachType.Dietitian]}
            >
              <PageSuspense>
                <GamePlanEditor state="view" />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="gameplanEditor"
          path="members/:memberId/gameplan/:planId/edit"
          element={
            <PrivateRoute
              allowedCoachTypes={[CoachType.Coach, CoachType.Dietitian]}
            >
              <PageSuspense>
                <GamePlanEditor state="edit" />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="gameplanTemplates"
          path="members/:memberId/gameplanTemplates"
          element={
            <PrivateRoute allowedCoachTypes={[CoachType.Coach]}>
              <PageSuspense>
                <GameplanTemplates />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="trainingPlanTemplates"
          path="members/:memberId/trainingPlan"
          element={
            <PrivateRoute allowedCoachTypes={[CoachType.Coach]}>
              <PageSuspense>
                <TrainingPlanTemplates />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="trainingPlanTemplates"
          path="members/:memberId/trainingPlan/:planId/"
          element={
            <PrivateRoute allowedCoachTypes={[CoachType.Coach]}>
              <PageSuspense>
                <CustomizeTrainingPlanTemplate />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="sessions"
          path="sessions"
          element={
            <PrivateRoute
              allowedCoachTypes={[CoachType.Coach, CoachType.Dietitian]}
            >
              <PageSuspense>
                <Sessions />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="classes"
          path="classes"
          element={
            <PrivateRoute
              allowedCoachTypes={[CoachType.Coach, CoachType.Dietitian]}
            >
              <PageSuspense>
                <Classes />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="methodology"
          path="methodology"
          element={
            <PrivateRoute
              allowedCoachTypes={[CoachType.Coach, CoachType.Dietitian]}
            >
              <PageSuspense>
                <Methodology />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="coachSettings"
          path="settings/:settingsPage"
          element={
            <PrivateRoute
              allowedCoachTypes={[CoachType.Coach, CoachType.Dietitian]}
            >
              <PageSuspense>
                <CoachSettings />
              </PageSuspense>
            </PrivateRoute>
          }
        />
      </Route>
    </Route>
    <Route
      path="settings"
      element={<Navigate to="/app/settings/availability" replace />}
    />
    {/* Redirect from /anything to /app - this is required in addition to the redirect above */}
    <Route path="*" element={<Navigate to="/app" replace />} />
  </Route>,
);

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);
const browserRouter = sentryCreateBrowserRouter(routesConfig);
export const BrowserRouter = () => <RouterProvider router={browserRouter} />;

// Browser router doesn't work in jest tests and we can't export non-react components here
// so we need to create a memory router for tests https://github.com/vitejs/vite/discussions/4583
const memoryRouter = (initialEntries: string[]) =>
  createMemoryRouter(routesConfig, {
    initialEntries,
  });
export const TestRouter = ({
  initialEntries,
}: {
  initialEntries: string[];
}) => <RouterProvider router={memoryRouter(initialEntries)} />;
